import { z } from 'zod';
import { modulePermission } from './modules';
import type { RoleCategoryId } from './role-categories';

/**
 * Plug-in middlewares (docs/plans/phase-2-plugins.md, DECISIONS 2026-10-10 "Plug-in
 * (middleware) design after M7"). A middleware is one source system (PowerSchool, …); its
 * modules are the app tiles. A middleware module's id is `<middleware>.<module>` and its
 * permission is `module.<middleware>.<module>.use` (modulePermission applied to the dotted id).
 */

/** Lowercase `a-z0-9-`, 2–30 characters, starting with a letter (plan §3). */
export const MIDDLEWARE_ID_PATTERN = /^[a-z][a-z0-9-]{1,29}$/;
export const middlewareIdSchema = z.string().regex(MIDDLEWARE_ID_PATTERN);

export type MiddlewareKind = 'connector' | 'sidecar';

/**
 * An extra permission a module declares besides `use` (none today: Timetable's `view_as` became
 * the app-wide `dev.view_as`, 2026-10-26; the mechanism stays for future ones). Its permission is
 * `module.<middleware>.<module>.<key>`; undeclared keys stay invalid. Action permissions are
 * privileged: treated like writes, so the Users category can never hold one.
 */
export interface MiddlewareModuleAction {
  /** Lowercase with underscores, like `users.assign_roles`; never `use`. */
  readonly key: string;
  readonly label: string;
}

export const MODULE_ACTION_KEY_PATTERN = /^[a-z][a-z0-9_]{1,39}$/;

export interface MiddlewareModuleEntry {
  readonly id: string;
  readonly title: string;
  readonly icon: string;
  /**
   * One-line description of the function, shown under the title on the default Home tile
   * (optional, additive 2026-10-26). Omitted = the tile shows icon + title only.
   */
  readonly tagline?: string;
  /** True when the module never changes school data (decides isWritePermission). */
  readonly readOnlySafe: boolean;
  /** Role categories that may hold the module's `use` permission. */
  readonly categories: readonly RoleCategoryId[];
  /** Extra permissions besides `use` (privileged; see MiddlewareModuleAction). */
  readonly actions?: readonly MiddlewareModuleAction[];
  /**
   * Proposed default grant (additive, 2026-10-26, Odoo plan §3): the role categories whose
   * seeded roles the demo seed grants `use` to. Omitted or empty = no default grant (the
   * Tenant Admin grants it on Admin › Roles). Never Users; always within `categories`. LIVE
   * seeds nothing from it: `tenant_admin` holds every permission anyway.
   */
  readonly defaultGrant?: readonly RoleCategoryId[];
}

/** Middlewares this release knows about. Adding one needs a version bump (plan §10). */
export const MIDDLEWARE_CATALOGUE = [
  {
    id: 'powerschool',
    title: 'PowerSchool',
    kind: 'connector',
    modules: [
      {
        id: 'timetable',
        title: 'Timetable',
        icon: 'calendar',
        readOnlySafe: true,
        categories: ['teachers', 'slt', 'local_admin', 'tenant_admin'],
      },
    ],
  },
  {
    // Odoo Time Off (docs/plans/odoo-timeoff.md): three tiles, own data only, plus Who is absent.
    id: 'odoo',
    title: 'Odoo',
    kind: 'connector',
    modules: [
      {
        // Profile merged in (owner, after the DEMO): the profile card sits on top.
        id: 'leave',
        title: 'My profile & leave',
        icon: 'id-card',
        tagline: 'Your details, approver and leave balances',
        readOnlySafe: true,
        categories: ['users', 'teachers', 'slt', 'local_admin', 'tenant_admin'],
        defaultGrant: ['teachers', 'slt', 'local_admin'],
      },
      {
        id: 'request',
        title: 'Leave request',
        icon: 'create',
        tagline: 'Ask for time off',
        // Creates hr.leave: a write, so Users can never hold it.
        readOnlySafe: false,
        categories: ['teachers', 'slt', 'local_admin', 'tenant_admin'],
        defaultGrant: ['teachers', 'slt', 'local_admin'],
      },
      {
        id: 'absences',
        title: 'Who is absent',
        icon: 'user-absent',
        tagline: 'Staff on leave today',
        readOnlySafe: true,
        // Health data (sick leave): designated people only, through a dedicated role.
        categories: ['teachers', 'slt', 'local_admin', 'tenant_admin'],
        defaultGrant: [],
      },
    ],
  },
] as const satisfies readonly {
  id: string;
  title: string;
  kind: MiddlewareKind;
  modules: readonly MiddlewareModuleEntry[];
}[];

type CatalogueEntry = (typeof MIDDLEWARE_CATALOGUE)[number];
export type MiddlewareId = CatalogueEntry['id'];

/** `<middleware>.<module>` for every catalogue module, e.g. `powerschool.timetable`. */
export type MiddlewareModuleId = {
  [M in CatalogueEntry as M['id']]: `${M['id']}.${M['modules'][number]['id']}`;
}[MiddlewareId];

export type MiddlewareModulePermission = `module.${MiddlewareModuleId}.use`;
/** `module.<middleware>.<module>.<action>` for a declared action, e.g. `…timetable.export`. */
export type MiddlewareModuleActionPermission = `module.${MiddlewareModuleId}.${string}`;

export const MIDDLEWARE_IDS: readonly MiddlewareId[] = MIDDLEWARE_CATALOGUE.map((m) => m.id);

export function isKnownMiddlewareId(id: string): id is MiddlewareId {
  return (MIDDLEWARE_IDS as readonly string[]).includes(id);
}

/** `middlewareModuleId('powerschool', 'timetable')` → `'powerschool.timetable'`. */
export function middlewareModuleId(middleware: string, module: string): `${string}.${string}` {
  return `${middleware}.${module}`;
}

/** One flattened middleware module, as the manifest, permissions and route guard see it. */
export interface MiddlewareModuleInfo extends MiddlewareModuleEntry {
  /** Dotted id, e.g. `powerschool.timetable` (manifest module id). */
  readonly id: MiddlewareModuleId;
  readonly middlewareId: MiddlewareId;
  readonly moduleId: string;
  /** "PowerSchool · Timetable" */
  readonly fullTitle: string;
  readonly permission: MiddlewareModulePermission;
  /** Declared actions with their permission ids. */
  readonly actionPermissions: readonly {
    readonly key: string;
    readonly label: string;
    readonly permission: MiddlewareModuleActionPermission;
  }[];
}

/** `moduleActionPermission('powerschool.timetable', 'export')` → `module.powerschool.timetable.export`. */
export function moduleActionPermission(
  moduleId: MiddlewareModuleId,
  key: string,
): MiddlewareModuleActionPermission {
  return `module.${moduleId}.${key}`;
}

export const MIDDLEWARE_MODULES: readonly MiddlewareModuleInfo[] = MIDDLEWARE_CATALOGUE.flatMap(
  (mw) =>
    mw.modules.map((m) => {
      const id = middlewareModuleId(mw.id, m.id) as MiddlewareModuleId;
      const actions: readonly MiddlewareModuleAction[] = (m as MiddlewareModuleEntry).actions ?? [];
      return {
        ...m,
        id,
        middlewareId: mw.id,
        moduleId: m.id,
        fullTitle: `${mw.title} · ${m.title}`,
        permission: modulePermission(id) as MiddlewareModulePermission,
        actionPermissions: actions.map((a) => ({
          key: a.key,
          label: a.label,
          permission: moduleActionPermission(id, a.key),
        })),
      };
    }),
);

export const MIDDLEWARE_MODULE_IDS: readonly MiddlewareModuleId[] = MIDDLEWARE_MODULES.map(
  (m) => m.id,
);

export const MIDDLEWARE_MODULE_PERMISSIONS: readonly MiddlewareModulePermission[] =
  MIDDLEWARE_MODULES.map((m) => m.permission);

/** Every declared module action permission (privileged; tenant_admin always holds them). */
export const MIDDLEWARE_MODULE_ACTION_PERMISSIONS: readonly MiddlewareModuleActionPermission[] =
  MIDDLEWARE_MODULES.flatMap((m) => m.actionPermissions.map((a) => a.permission));

export function isModuleActionPermission(permission: string): boolean {
  return (MIDDLEWARE_MODULE_ACTION_PERMISSIONS as readonly string[]).includes(permission);
}

/** The module that declares this action permission. */
export function findMiddlewareModuleByActionPermission(
  permission: string,
): MiddlewareModuleInfo | undefined {
  return MIDDLEWARE_MODULES.find((m) =>
    m.actionPermissions.some((a) => a.permission === permission),
  );
}

/** The categories the demo seed grants this module's `use` to ([] = none; see defaultGrant). */
export function defaultGrantCategoriesFor(id: string): readonly RoleCategoryId[] {
  return findMiddlewareModule(id)?.defaultGrant ?? [];
}

export function findMiddlewareModule(id: string): MiddlewareModuleInfo | undefined {
  return MIDDLEWARE_MODULES.find((m) => m.id === id);
}

export function findMiddlewareModuleByPermission(
  permission: string,
): MiddlewareModuleInfo | undefined {
  return MIDDLEWARE_MODULES.find((m) => m.permission === permission);
}

/** The middleware a dotted module id belongs to (`powerschool.timetable` → `powerschool`). */
export function middlewareOfModule(id: string): string | null {
  const dot = id.indexOf('.');
  return dot > 0 ? id.slice(0, dot) : null;
}
